import { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Stack,
  Typography,
  IconButton,
  SvgIconTypeMap,
} from "@mui/material";
import { PriceDisplayBox } from "../../inventory/components/PriceDisplayBox";
import { PriceInformation } from "./types";
import { useTranslation } from "../../../hooks/useTranslation";
import {
  MonetizationOn,
  ShoppingCart,
  AccountBalance,
  Info,
  AttachMoney,
} from "@mui/icons-material";
import { AmazonIcon } from "./icons/AmazonIcon";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { getTotalCost } from "./utils";
type PriceDetailDialogProps = {
  minPriceInfo?: PriceInformation;
  defaultPriceInfo?: PriceInformation;
  maxPriceInfo?: PriceInformation;
};

type PriceDetail = {
  label: string;
  price?: string | number;
  percentage?: number;
  color?: string;
  MuiIcon: OverridableComponent<SvgIconTypeMap<{ fontSize?: string }, "svg">>;
  SecondIcon?: OverridableComponent<
    SvgIconTypeMap<{ fontSize?: string }, "svg">
  >;
};

const getPriceDetails = (
  priceInfo: PriceInformation | undefined,
  translate: (_key: string) => string,
): PriceDetail[] => {
  const details: PriceDetail[] = [
    {
      label: translate("totalCost"),
      price: getTotalCost(priceInfo),
      MuiIcon: AttachMoney,
    },
    {
      label: translate("profit"),
      price: priceInfo?.profit,
      percentage: priceInfo?.profitPercentage,
      MuiIcon: MonetizationOn,
      color: "green",
    },
    {
      label: translate("amazonCommission"),
      price: priceInfo?.amazonCommission,
      percentage: priceInfo?.amazonCommissionPercentage,
      MuiIcon: AmazonIcon,
    },
    {
      label: translate("finalPrice"),
      price: priceInfo?.finalPrice,
      MuiIcon: ShoppingCart,
      color: "green",
    },
  ];

  if (priceInfo?.tax) {
    details.splice(details.length - 1, 0, {
      label: translate("tax"),
      price: priceInfo?.tax,
      percentage: priceInfo?.taxPercentage,
      MuiIcon: AccountBalance,
    });
  }

  return details;
};

const PriceDetailTable = ({
  priceDetails,
  title,
}: {
  priceDetails: PriceDetail[];
  title: string;
}) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      {title}
    </Typography>
    <Table size="small" sx={{ minWidth: "400px" }}>
      <TableBody>
        {priceDetails.map((detail) => {
          return (
            <TableRow key={detail.label}>
              <TableCell>{detail.label}</TableCell>
              <TableCell>
                <PriceDisplayBox
                  price={detail.price}
                  percentage={detail.percentage}
                  color={detail.color ?? "#bb2222"}
                  StartIcon={detail.MuiIcon}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </Box>
);

export const PriceDetailDialog = ({
  minPriceInfo,
  defaultPriceInfo,
  maxPriceInfo,
}: PriceDetailDialogProps) => {
  const [open, setOpen] = useState(false);
  const { translate, translateWithoutPrefix } = useTranslation("pages.product");

  const minPriceDetails = useMemo(
    () => getPriceDetails(minPriceInfo, translate),
    [minPriceInfo, translate],
  );
  const defaultPriceDetails = useMemo(
    () => getPriceDetails(defaultPriceInfo, translate),
    [defaultPriceInfo, translate],
  );
  const maxPriceDetails = useMemo(
    () => getPriceDetails(maxPriceInfo, translate),
    [maxPriceInfo, translate],
  );

  return (
    <>
      <IconButton size="small" onClick={() => setOpen(true)}>
        <Info color="primary" />
      </IconButton>

      <Dialog maxWidth={"xl"} open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{translate("productCostDetails")}</DialogTitle>

        <DialogContent>
          <Stack direction="row" spacing={3}>
            <PriceDetailTable
              priceDetails={minPriceDetails}
              title={translate("minPrice")}
            />
            <PriceDetailTable
              priceDetails={defaultPriceDetails}
              title={translate("price")}
            />
            <PriceDetailTable
              priceDetails={maxPriceDetails}
              title={translate("maxPrice")}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            {translateWithoutPrefix("buttons.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
